/**
 * Time picker — three-dropdown style (hour, minute, AM/PM or 24h).
 * Reads time format preference from TravelSettings.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Clock } from 'lucide-react';
import { getTravelSettings, formatTime } from './TravelSettings';

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/** Parse a "HH:MM" string into { hour24, minute }. Returns null if invalid. */
function parse24(value: string): { hour24: number; minute: number } | null {
  const m = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return { hour24: parseInt(m[1], 10), minute: parseInt(m[2], 10) };
}

/** Convert 24h hour to 12h components. */
function to12(hour24: number): { hour12: number; period: 'AM' | 'PM' } {
  const period: 'AM' | 'PM' = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  return { hour12, period };
}

/** Convert 12h components back to 24h hour. */
function to24(hour12: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
}

function build24(hour24: number, minute: number): string {
  return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1–12
const HOURS_24 = Array.from({ length: 24 }, (_, i) => i); // 0–23

export function TimePicker({ value, onChange, placeholder = 'Time', className }: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const settings = getTravelSettings();
  const is24 = settings.timeFormat === '24h';
  const minuteInterval = settings.minuteInterval ?? 5;
  const minutes = Array.from(
    { length: Math.ceil(60 / minuteInterval) },
    (_, i) => i * minuteInterval,
  );

  const parsed = value ? parse24(value) : null;
  const currentH24 = parsed?.hour24 ?? (is24 ? 0 : 12);
  const currentMin = parsed?.minute ?? 0;
  const { hour12, period } = to12(currentH24);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, handleClickOutside]);

  const emit = (h24: number, min: number) => {
    onChange(build24(h24, min));
  };

  const handleHourChange = (h: number) => {
    if (is24) {
      emit(h, currentMin);
    } else {
      emit(to24(h, period), currentMin);
    }
  };

  const handleMinuteChange = (m: number) => {
    emit(currentH24, m);
  };

  const handlePeriodChange = (p: 'AM' | 'PM') => {
    emit(to24(hour12, p), currentMin);
  };

  const displayValue = value ? formatTime(value) : '';

  return (
    <div className={`relative ${className || ''}`} ref={ref}>
      <div className="relative">
        <button
          type="button"
          className="flex items-center w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={() => setOpen(!open)}
        >
          <span className={displayValue ? 'text-foreground' : 'text-muted-foreground'}>
            {displayValue || placeholder}
          </span>
          <Clock className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-50 top-full mt-1 left-0 bg-popover border border-border rounded-md shadow-lg p-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-1 items-center">
            {/* Hour */}
            <div className="flex flex-col items-center">
              <span className="text-[10px] text-muted-foreground mb-1">Hr</span>
              <select
                value={is24 ? currentH24 : hour12}
                onChange={(e) => handleHourChange(parseInt(e.target.value, 10))}
                className="h-8 w-14 rounded border border-input bg-popover text-popover-foreground text-sm text-center cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring appearance-auto"
              >
                {(is24 ? HOURS_24 : HOURS_12).map((h) => (
                  <option key={h} value={h}>
                    {is24 ? String(h).padStart(2, '0') : h}
                  </option>
                ))}
              </select>
            </div>

            <span className="text-muted-foreground font-bold mt-4">:</span>

            {/* Minute */}
            <div className="flex flex-col items-center">
              <span className="text-[10px] text-muted-foreground mb-1">Min</span>
              <select
                value={currentMin}
                onChange={(e) => handleMinuteChange(parseInt(e.target.value, 10))}
                className="h-8 w-14 rounded border border-input bg-popover text-popover-foreground text-sm text-center cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring appearance-auto"
              >
                {minutes.map((m) => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>

            {/* AM/PM (only in 12h mode) */}
            {!is24 && (
              <div className="flex flex-col items-center">
                <span className="text-[10px] text-muted-foreground mb-1">&nbsp;</span>
                <select
                  value={period}
                  onChange={(e) => handlePeriodChange(e.target.value as 'AM' | 'PM')}
                  className="h-8 w-14 rounded border border-input bg-popover text-popover-foreground text-sm text-center cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring appearance-auto"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            )}
          </div>

          {/* Clear button */}
          {value && (
            <button
              className="mt-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear time
            </button>
          )}
        </div>
      )}
    </div>
  );
}
